/**
 * Generic in-memory cache manager with TTL support.
 *
 * This utility centralizes cache logic that was previously duplicated across
 * multiple Redux slices and hooks (researchSessionSlice, history, filters, usePreviewContent).
 *
 * Features:
 * - Generic type support for any data type
 * - Optional TTL (time-to-live) with automatic expiration
 * - Consistent logging format across all caches
 * - Debug statistics for monitoring cache usage
 *
 * @example
 * ```typescript
 * // Create a cache with 5-minute TTL
 * const userCache = new CacheManager<User>('UserCache', 5 * 60 * 1000);
 *
 * // Create a cache with no TTL (entries never expire)
 * const documentCache = new CacheManager<Document>('DocumentCache');
 *
 * // Usage
 * userCache.set('user-123', userData);
 * const user = userCache.get('user-123'); // Returns userData or null if expired
 * userCache.invalidate('user-123');
 * userCache.clear();
 * console.log(userCache.stats());
 * ```
 */
export class CacheManager<T> {
  private cache = new Map<string, { data: T; timestamp: number }>();

  /**
   * Create a new cache manager instance.
   *
   * @param name - Display name for logging (e.g., 'ChatHistory', 'SessionList')
   * @param ttlMs - Time-to-live in milliseconds. 0 = no expiration (default)
   */
  constructor(
    private name: string,
    private ttlMs = 0
  ) {}

  /**
   * Get a cached value by key.
   * Returns null if the key doesn't exist or if the entry has expired.
   *
   * @param key - Cache key to look up
   * @returns The cached data or null
   */
  get(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;

    // Check TTL expiration (only if TTL is set)
    if (this.ttlMs > 0 && Date.now() - entry.timestamp > this.ttlMs) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Get the age of a cached entry in milliseconds.
   * Returns null if the key doesn't exist.
   *
   * @param key - Cache key to check
   * @returns Age in milliseconds or null
   */
  getAge(key: string): number | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    return Date.now() - entry.timestamp;
  }

  /**
   * Check if a key exists in the cache (and is not expired).
   *
   * @param key - Cache key to check
   * @returns true if the key exists and is valid
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Store a value in the cache.
   *
   * @param key - Cache key
   * @param data - Data to cache
   */
  set(key: string, data: T): void {
    this.cache.set(key, { data, timestamp: Date.now() });
  }

  /**
   * Remove a specific entry from the cache.
   *
   * @param key - Cache key to invalidate
   */
  invalidate(key: string): void {
    this.cache.delete(key);
  }

  /**
   * Remove all entries from the cache.
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Get cache statistics for debugging.
   *
   * @returns Object with size and keys
   */
  stats(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys()),
    };
  }

  /**
   * Get the configured TTL in milliseconds.
   */
  getTtl(): number {
    return this.ttlMs;
  }

  /**
   * Get the cache name (for debugging).
   */
  getName(): string {
    return this.name;
  }
}
